import { FocusEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InputMask from 'react-input-mask'
import isString from 'lodash/isString'
import isUndefined from 'lodash/isUndefined'
import { Moment } from 'moment'
import more from '@assets/images/more.svg'
import {
  DatePickerRange,
  DateRangeValue,
  DateTimePicker,
  TextField,
} from '@microservices/wiskey-react-components'
import { Box, SelectChangeEvent } from '@mui/material'
import { useSearchAssistantContext } from '@widgets/SearchAssistant/context'
import { getDatePickerRangePlaceholderCriteria } from '@widgets/SearchAssistant/utils/getDatePickerRangePlaceholderCriteria'

import { CustomSvgIcon } from '@components/CustomSvgIcon'
import { MappedCheckbox } from '@components/MappedCheckbox'
import { SelectBoolean } from '@components/SelectBoolean'
import { EnumAutocomplete } from '@components/ui/EnumAutocomplete'
import { GroupAutocomplete } from '@components/ui/GroupAutocomplete'
import { ObjectPicker } from '@components/ui/ObjectPicker'

import { generalFieldStylesByMode, getPlaceholderByField, isObjectValueType } from '@helpers'
import {
  AUTOCOMPLETE_OPTIONS_DAILY_RANGE,
  CriteriaStoredValue,
  DATE_RANGE_POSITION,
  FIELD_VALUE_FORMAT,
  FIELD_VALUE_TYPE,
  FILTERS_DIRTY_TYPES,
  FILTERS_SA,
  FILTERS_SA_TYPE,
  OBJECT_FIELD_TYPE,
  REGEX,
  SEARCH_FIELD_TYPE,
  ValueAddType,
  ValueType,
} from '@constants'
import { AutocompleteOption } from '@types'

import { SearchAssistantDailyRangeDatePicker } from '../SearchAssistantDailyRangeDatePicker/SearchAssistantDailyRangeDatePicker'
import {
  RelativeType,
  SearchAssistantRelativeDatePicker,
} from '../SearchAssistantRelativeDatePicker/SearchAssistantRelativeDatePicker'
import {
  SearchAssistantStartAdornmentFilterRules,
  SearchRuleType,
} from '../SearchAssistantStartAdornmentFilterRules/SearchAssistantStartAdornmentFilterRules'

interface SearchAssistantInputValueProps {
  currentValue: CriteriaStoredValue
  addValue?: (value: ValueAddType) => void
  codeCriteria: string
  fieldType?: SEARCH_FIELD_TYPE
  inputFormat?: string
  disabled?: boolean
  // Для obj_embedded полей нужны linkedObjectCode и objectValue
  linkedObjectCode?: string
  objectValue?: string
  onObjectNotFoundError?: (value: boolean) => void
  onChange: (value: ValueType) => void
}

const SearchAssistantInputValue: React.FC<SearchAssistantInputValueProps> = props => {
  const {
    addValue,
    currentValue,
    codeCriteria,
    fieldType,
    inputFormat,
    linkedObjectCode = '',
    objectValue = '',
    disabled,
    onObjectNotFoundError,
    onChange,
  } = props

  const { t } = useTranslation()
  const [searchRule, setSearchRule] = useState<SearchRuleType>(
    FILTERS_SA[currentValue.searchRule || FILTERS_SA_TYPE.CONTAIN].value
  )
  const [dailyRangeValue, setDailyRangeValue] = useState<AutocompleteOption | null>({
    id: AUTOCOMPLETE_OPTIONS_DAILY_RANGE.TODAY,
    label: AUTOCOMPLETE_OPTIONS_DAILY_RANGE.TODAY,
  })
  const disabledInput = !FILTERS_DIRTY_TYPES.includes(searchRule.id)

  const context = useSearchAssistantContext('SearchAssistantInputValue')

  const getDefaultValue = () => {
    switch (currentValue.valueType) {
      case FIELD_VALUE_TYPE.DATETIME:
        return null
      case FIELD_VALUE_TYPE.OBJ_EMBEDDED:
      case FIELD_VALUE_TYPE.OBJ_PK_LINK:
      case FIELD_VALUE_TYPE.OBJ_INTERNAL_ID_LINK:
        return []
      default:
        return ''
    }
  }
  const [value, setValue] = useState<ValueType>(getDefaultValue())
  const placeholder = useMemo(() => getPlaceholderByField(currentValue), [currentValue])

  useEffect(() => {
    onChange(value)
  }, [value])

  useEffect(() => {
    setSearchRule(FILTERS_SA[currentValue.searchRule || FILTERS_SA_TYPE.EQUAL].value)
    if (isObjectValueType(currentValue.valueType)) {
      setValue(currentValue.value ? currentValue.value : [])

      return
    }
    if (currentValue.type === OBJECT_FIELD_TYPE.ENUM) {
      setValue(currentValue.value?.toString() ? currentValue.value : null)

      return
    }

    setValue(currentValue.value)

    if (
      currentValue.fieldType === SEARCH_FIELD_TYPE.DAILY_RANGE &&
      currentValue.valueType === FIELD_VALUE_TYPE.DATETIME
    ) {
      if (!currentValue.value) {
        setDailyRangeValue(null)
      } else {
        setDailyRangeValue({
          id: currentValue?.dailyRange
            ? currentValue?.dailyRange.id
            : (currentValue?.value as string),
          label: currentValue?.dailyRange
            ? currentValue?.dailyRange.label
            : (currentValue?.value as string),
        })
      }
    }
  }, [currentValue])

  const handleBlur = (
    eventValue?: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    directEventValue?: AutocompleteOption
  ) => {
    if (directEventValue) {
      if (currentValue.type === OBJECT_FIELD_TYPE.ENUM) {
        addValue?.({
          codeCriteria,
          searchRule: searchRule.id,
          value: directEventValue.id,
          valueId: currentValue.id,
        })

        return
      }
    }

    if (eventValue) {
      if (!eventValue.currentTarget.contains(eventValue.relatedTarget)) {
        if (eventValue.currentTarget.value.trim()) {
          if (currentValue.valueType === FIELD_VALUE_TYPE.STRING && isString(value)) {
            addValue?.({
              codeCriteria,
              searchRule: searchRule.id,
              value: value.trim(),
              valueId: currentValue.id,
            })

            return
          }
          addValue?.({ codeCriteria, searchRule: searchRule.id, value, valueId: currentValue.id })

          return
        }
        addValue?.({ codeCriteria, searchRule: searchRule.id, valueId: currentValue.id, value: '' })
      }
    }
  }

  const handleBlurDate = (dateValue?: string | [DateRangeValue, DateRangeValue] | null) => {
    // проверка для DateRangePicker
    if (Array.isArray(dateValue)) {
      addValue?.({
        searchRule: searchRule.id,
        codeCriteria,
        value: !dateValue[0] && !dateValue[1] ? null : dateValue,
        valueId: currentValue.id,
      })
      setValue(!dateValue[0] && !dateValue[1] ? null : dateValue)

      return
    }
    // обычный DatePicker
    if (!isUndefined(dateValue)) {
      addValue?.({
        codeCriteria,
        searchRule: searchRule.id,
        value: dateValue,
        valueId: currentValue.id,
      })
      setValue(dateValue)
    }
  }

  const handleChange = (targetValue: string) => {
    const type = currentValue.valueType

    if (
      (type === FIELD_VALUE_TYPE.INTEGER && REGEX.ONLY_NUMBER.test(targetValue)) ||
      type === FIELD_VALUE_TYPE.DOUBLE
    ) {
      addValue?.({
        codeCriteria,
        searchRule: searchRule.id,
        value: targetValue,
        valueId: currentValue.id,
      })

      setValue(targetValue ? Number(targetValue) : '')

      return
    }
    addValue?.({
      codeCriteria,
      searchRule: searchRule.id,
      value: targetValue,
      valueId: currentValue.id,
    })

    setValue(targetValue)

    return
  }

  const handleChangeDate = (
    dateValue?: string | [DateRangeValue, DateRangeValue],
    rule?: string,
    dailyRange?: AutocompleteOption
  ) => {
    if (dailyRange) {
      setValue(dailyRange)
      addValue?.({
        codeCriteria,
        searchRule: (rule || searchRule.id) as FILTERS_SA_TYPE,
        value: dailyRange.id,
        valueId: currentValue.id,
        dailyRange,
      })

      return
    }
    if (dateValue) {
      const value = Array.isArray(dateValue) && dateValue.every(value => !value) ? '' : dateValue

      setValue(value)
      // if (value) {
      addValue?.({
        codeCriteria,
        value,
        valueId: currentValue.id,
        searchRule: (rule || searchRule.id) as FILTERS_SA_TYPE,
      })
      setValue(dateValue)
      // }

      return
    }

    setValue('')

    addValue?.({
      codeCriteria,
      value: '',
      valueId: currentValue.id,
      searchRule: (rule || searchRule.id) as FILTERS_SA_TYPE,
    })
  }

  const handleChangeObjEmbedded = (value: AutocompleteOption | null | AutocompleteOption[]) => {
    if (Array.isArray(value) && value.length === 0) {
      addValue?.({
        codeCriteria,
        searchRule: searchRule.id,
        value: null,
        valueId: currentValue.id,
      })

      return
    }

    addValue?.({
      codeCriteria,
      searchRule: searchRule.id,
      value,
      valueId: currentValue.id,
    })
  }

  const handleMappedCheckboxChange = (value: number) => {
    addValue?.({
      codeCriteria,
      searchRule: searchRule.id,
      value: `${value}`,
      valueId: currentValue.id,
    })
    setValue(value)
  }

  const handleChangeBooleanSelect = ({ target: { value } }: SelectChangeEvent<string | number>) => {
    addValue?.({
      codeCriteria,
      searchRule: searchRule.id,
      value: `${value}`,
      valueId: currentValue.id,
    })
    setValue(value)
  }

  const handleChangeEnum = (value: AutocompleteOption | null) => {
    setValue(value)
    addValue?.({
      codeCriteria,
      searchRule: searchRule.id,
      value: value?.id.toString() ? value.id : null,
      valueId: currentValue.id,
    })
  }

  const handleChangeGroups = (value: AutocompleteOption[] | null) => {
    if (Array.isArray(value) && value.length === 0) {
      addValue?.({
        codeCriteria,
        value: null,
        valueId: currentValue.id,
        // TODO: узнать какие правила поиска для групп
        searchRule: searchRule.id,
      })

      return
    }

    addValue?.({
      codeCriteria,
      value,
      valueId: currentValue.id,
      // TODO: узнать какие правила поиска для групп
      searchRule: searchRule.id,
    })
  }

  const handleChangeRelativeDate = (value: [DateRangeValue, string, string]) => {
    setValue(value)
    addValue?.({
      codeCriteria,
      searchRule: searchRule.id,
      value: value.some(Boolean) ? value : null,
      valueId: currentValue.id,
    })
  }

  const handleValidateValue = (value: string) => {
    if (
      currentValue.objectValue &&
      REGEX.REF_BY_ID.test(currentValue.objectValue) &&
      !(
        currentValue.valueType === FIELD_VALUE_TYPE.OBJ_PK_LINK ||
        currentValue.valueType === FIELD_VALUE_TYPE.OBJ_INTERNAL_ID_LINK ||
        currentValue.isFilterByObject
      )
    ) {
      return false
    }

    return REGEX.ASSISTANT_FORBIDDEN_CHARS.test(value)
  }

  const handleSetSearchRules = (newSearchRule: SearchRuleType) => {
    setSearchRule(newSearchRule)
    addValue?.({
      codeCriteria,
      searchRule: newSearchRule.id,
      value: currentValue.value,
      valueId: currentValue.id,
    })
  }

  const handleCheckboxReset = () => {
    addValue?.({
      codeCriteria,
      value: '',
      searchRule: FILTERS_SA_TYPE.EQUAL,
    })
  }

  const autocompleteOptionsDailyRange = Object.values(AUTOCOMPLETE_OPTIONS_DAILY_RANGE).map(
    option => ({ id: option, label: option })
  )

  const handleChangeDailyRange = (value: AutocompleteOption) => {
    setDailyRangeValue(value)
    handleChangeDate(undefined, FILTERS_SA_TYPE.EQUAL, value)

    return
  }

  const renderInput = () => {
    if (currentValue.isFilterByObject || isObjectValueType(currentValue.valueType)) {
      const isHidePlaceholder = value && (!Array.isArray(value) || Boolean(value.length))

      return (
        <ObjectPicker
          multiple
          disableClearable={false}
          disabled={disabled || disabledInput}
          dropDownList={currentValue.dropDownList}
          dropDownWindow={currentValue.dropDownWindow}
          formObjectCode={currentValue.objectCode}
          formatDate={inputFormat}
          handleSetDropdownWindowOpenFormState={context.onChangeDropDownWindowOpen}
          isDialogWindow={context.isInDialogWindow}
          isDropDownList={Boolean(currentValue.dropDownList)}
          isDropDownWindow={Boolean(currentValue.dropDownWindow)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          isSearchAssistiant={true}
          name={currentValue.name}
          objectCode={currentValue.isFilterByObject ? currentValue.objectCode : linkedObjectCode}
          objectValue={objectValue}
          pathArray={currentValue.pathArray}
          placeholder={isHidePlaceholder ? undefined : placeholder}
          value={value as AutocompleteOption | null | string}
          valueId={currentValue.id}
          valueType={currentValue.valueType}
          inputProps={{
            startAdornment: (
              <SearchAssistantStartAdornmentFilterRules
                disabled={disabled}
                fieldType={currentValue.valueType}
                name={currentValue.name}
                objectFieldType={currentValue.type}
                // eslint-disable-next-line react/jsx-handler-names
                setValue={handleSetSearchRules}
                value={searchRule}
              />
            ),
          }}
          popupIcon={
            <Box height={'100%'} width={24}>
              <CustomSvgIcon src={more} sx={{ fontSize: '1rem' }} />
            </Box>
          }
          sx={{
            '.MuiAutocomplete-popupIndicator': { marginRight: '10px' },
            '& .MuiButtonBase-root': { transform: 'none' },
          }}
          onChange={handleChangeObjEmbedded}
          onValidateInputValue={handleValidateValue}
          onChangeMultipleValueDropdownEntity={handleChangeObjEmbedded}
          // eslint-disable-next-line react/jsx-handler-names
          onObjectNotFoundError={onObjectNotFoundError}
        />
      )
    }

    if (currentValue.isGroupRelated && currentValue.linkedObjectCode) {
      return (
        <GroupAutocomplete
          disabled={Boolean(disabled) || disabledInput}
          isDialogWindow={context.isInDialogWindow ?? false}
          name={currentValue.name}
          objectCode={currentValue.linkedObjectCode}
          placeholder={placeholder}
          value={value as AutocompleteOption}
          inputProps={{
            startAdornment: (
              <SearchAssistantStartAdornmentFilterRules
                disabled={disabled}
                fieldType={currentValue.valueType}
                name={currentValue.name}
                objectFieldType={currentValue.type}
                // eslint-disable-next-line react/jsx-handler-names
                setValue={handleSetSearchRules}
                value={searchRule}
              />
            ),
          }}
          onChange={handleChangeGroups}
        />
      )
    }

    if (currentValue.type === OBJECT_FIELD_TYPE.ENUM) {
      return (
        <EnumAutocomplete
          disabled={disabled || disabledInput}
          fieldCode={currentValue.name}
          isDialogWindow={context.isInDialogWindow}
          name={currentValue.name}
          objectCode={currentValue.objectCode ?? ''}
          placeholder={placeholder}
          smallPopupIconPadding={false}
          value={value as AutocompleteOption}
          valueType={currentValue.valueType as FIELD_VALUE_TYPE}
          inputProps={{
            startAdornment: (
              <SearchAssistantStartAdornmentFilterRules
                disabled={disabled}
                fieldType={currentValue.valueType}
                name={currentValue.name}
                objectFieldType={currentValue.type}
                // eslint-disable-next-line react/jsx-handler-names
                setValue={handleSetSearchRules}
                value={searchRule}
              />
            ),
          }}
          popupIcon={
            <Box height={'100%'} width={20}>
              <CustomSvgIcon src={more} sx={{ fontSize: '1rem' }} />
            </Box>
          }
          onChange={handleChangeEnum}
        />
      )
    }

    const asCheckbox =
      currentValue.valueFormat === FIELD_VALUE_FORMAT.NUMBER ||
      currentValue.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN

    if (asCheckbox && currentValue.valueFormat) {
      let mappedValue = value

      // Нужна дополнительная проверка для value во избежание конфузов приведения строки к boolean в ассистенте
      if (value === 'false' && currentValue.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN) {
        mappedValue = false as unknown as ValueType
      }

      if (value === 'true' && currentValue.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN) {
        mappedValue = true as unknown as ValueType
      }

      return (
        <MappedCheckbox
          catchOuterValueChanges
          ignoreErrorStyles
          resetByButton
          outerValue={mappedValue as number | boolean}
          // eslint-disable-next-line react/jsx-handler-names
          setOuterValue={value => handleMappedCheckboxChange(value as number)}
          valueFormat={currentValue.valueFormat}
          checkboxSx={{
            top: 0,
          }}
          checkboxWrapperSx={{
            ml: '1px',
          }}
          onReset={handleCheckboxReset}
        />
      )
    }

    if (currentValue.valueFormat === FIELD_VALUE_FORMAT.TIME) {
      return (
        <InputMask
          disabled={disabled || disabledInput}
          mask='99:59'
          maskChar='-'
          // @ts-expect-error TODO(isilanov): fix type for InputMask
          value={value}
          formatChars={{
            5: '[0-5]',
            9: '[0-9]',
          }}
          onBlur={(event: FocusEvent<HTMLInputElement>) =>
            handleChange(event.currentTarget?.value.replaceAll('-', '0'))
          }
          onChange={(event: FocusEvent<HTMLInputElement>) =>
            handleChange(event.currentTarget?.value)
          }
        >
          {() => (
            <TextField
              fullWidth
              disabled={disabled || disabledInput}
              placeholder={placeholder}
              size='small'
              type='text'
              InputProps={{
                inputProps: { sx: { py: 1 } },
                startAdornment: (
                  <SearchAssistantStartAdornmentFilterRules
                    disabled={disabled}
                    fieldType={currentValue.valueType}
                    name={currentValue.name}
                    objectFieldType={currentValue.type}
                    // eslint-disable-next-line react/jsx-handler-names
                    setValue={handleSetSearchRules}
                    value={searchRule}
                  />
                ),
              }}
              sx={theme => ({
                ...generalFieldStylesByMode(
                  // @ts-expect-error TODO(isilanov): fix type for theme
                  theme,
                  undefined,
                  undefined,
                  undefined,
                  context.isInDialogWindow
                ),
              })}
            />
          )}
        </InputMask>
      )
    }

    switch (currentValue.valueType) {
      case FIELD_VALUE_TYPE.INTEGER:
      case FIELD_VALUE_TYPE.DOUBLE:
        return (
          <TextField
            fullWidth
            disabled={disabled || disabledInput}
            placeholder={placeholder}
            size='small'
            type='number'
            value={value}
            InputProps={{
              inputProps: { sx: { py: 1 } },
              startAdornment: (
                <SearchAssistantStartAdornmentFilterRules
                  disabled={disabled}
                  fieldType={currentValue.valueType}
                  name={currentValue.name}
                  objectFieldType={currentValue.type}
                  // eslint-disable-next-line react/jsx-handler-names
                  setValue={handleSetSearchRules}
                  value={searchRule}
                />
              ),
            }}
            sx={theme => ({
              ...generalFieldStylesByMode(
                // @ts-expect-error TODO(isilanov): fix type for theme
                theme,
                undefined,
                undefined,
                undefined,
                context.isInDialogWindow
              ),
            })}
            onBlur={handleBlur}
            onChange={e => handleChange(e.currentTarget?.value)}
          />
        )
      case FIELD_VALUE_TYPE.STRING:
        return (
          <TextField
            fullWidth
            disabled={disabled || disabledInput}
            placeholder={placeholder}
            size='small'
            value={value}
            InputProps={{
              inputProps: { sx: { py: 1 } },
              startAdornment: (
                <SearchAssistantStartAdornmentFilterRules
                  disabled={disabled}
                  fieldType={currentValue.valueType}
                  name={currentValue.name}
                  objectFieldType={currentValue.type}
                  // eslint-disable-next-line react/jsx-handler-names
                  setValue={handleSetSearchRules}
                  value={searchRule}
                />
              ),
            }}
            sx={theme => ({
              ...generalFieldStylesByMode(
                // @ts-expect-error TODO(isilanov): fix type for theme
                theme,
                undefined,
                undefined,
                undefined,
                context.isInDialogWindow
              ),
            })}
            // onBlur={handleBlur} // Зачем тут onBlur?
            // eslint-disable-next-line react/jsx-handler-names
            onChange={e => {
              const value = e.currentTarget?.value

              if (handleValidateValue(value)) {
                handleChange(value.trim())
              }
            }}
          />
        )
      case FIELD_VALUE_TYPE.DATETIME:
        if (fieldType === SEARCH_FIELD_TYPE.RANGE_OF_VALUES) {
          const placeholderStart = getDatePickerRangePlaceholderCriteria(
            currentValue,
            DATE_RANGE_POSITION.START
          )
          const placeholderEnd = getDatePickerRangePlaceholderCriteria(
            currentValue,
            DATE_RANGE_POSITION.END
          )

          return (
            <DatePickerRange
              fullWidth
              popoverDateShiftMode
              disabled={disabled}
              inputFormat={inputFormat}
              placeholderEnd={placeholderEnd}
              value={value as Moment[] | null | undefined}
              sx={theme => ({
                ...generalFieldStylesByMode(
                  // @ts-expect-error TODO(isilanov): fix type for theme
                  theme,
                  undefined,
                  undefined,
                  undefined,
                  context.isInDialogWindow
                ),
              })}
              onBlur={handleBlurDate}
              onChange={handleChangeDate}
              placeholderStart={placeholderStart}
              // @ts-expect-error TODO(isilanov): fix type for sxPopover
              sxPopover={{ '& > .MuiPaper-root > .MuiBox-root': { width: 500 } }}
            />
          )
        }
        if (fieldType === SEARCH_FIELD_TYPE.RELATIVE_VALUE) {
          return (
            <SearchAssistantRelativeDatePicker
              popoverDateShiftMode
              disabled={disabled}
              inputFormat={inputFormat}
              isDialogWindow={context.isInDialogWindow}
              max={currentValue.max}
              min={currentValue.min}
              placeholder={placeholder}
              value={(value || [null, '', '']) as RelativeType}
              onChange={handleChangeRelativeDate}
            />
          )
        }
        if (fieldType === SEARCH_FIELD_TYPE.DAILY_RANGE) {
          return (
            <SearchAssistantDailyRangeDatePicker
              autocompleteOptionsDailyRange={autocompleteOptionsDailyRange}
              dailyRangeValue={dailyRangeValue}
              disabled={disabled}
              // eslint-disable-next-line react/jsx-handler-names
              handleChangeDailyRange={handleChangeDailyRange}
              isSearchAssistantDialogWindow={context.isInDialogWindow}
            />
          )
        }

        return (
          <DateTimePicker
            fullWidth
            popoverDateShiftMode
            disabled={disabled}
            inputFormat={inputFormat}
            placeholder={placeholder}
            sxPopover={{ '& > .MuiPaper-root > .MuiBox-root': { width: 500 } }}
            value={(value as string) || null}
            sx={theme => ({
              ...generalFieldStylesByMode(
                // @ts-expect-error TODO(isilanov): fix type for theme
                theme,
                undefined,
                undefined,
                undefined,
                context.isInDialogWindow
              ),
            })}
            onBlur={handleBlurDate}
            // @ts-expect-error TODO(isilanov): fix type for onChange
            onChange={handleChangeDate}
          />
        )
      case FIELD_VALUE_TYPE.BOOLEAN:
        return (
          <SelectBoolean
            disabled={disabled}
            isDialog={context.isInDialogWindow}
            placeholder={placeholder}
            value={value}
            startAdornment={
              <SearchAssistantStartAdornmentFilterRules
                disabled={disabled}
                fieldType={currentValue.valueType}
                name={currentValue.name}
                objectFieldType={currentValue.type}
                // eslint-disable-next-line react/jsx-handler-names
                setValue={handleSetSearchRules}
                value={searchRule}
              />
            }
            onChange={handleChangeBooleanSelect}
          />
        )

      default:
        return (
          <TextField
            fullWidth
            InputProps={{ inputProps: { sx: { py: 1 } } }}
            disabled={disabled || disabledInput}
            placeholder={placeholder}
            size='small'
            value={value}
            sx={theme => ({
              ...generalFieldStylesByMode(
                // @ts-expect-error TODO(isilanov): fix type for theme
                theme,
                undefined,
                undefined,
                undefined,
                context.isInDialogWindow
              ),
            })}
            onBlur={handleBlur}
            // eslint-disable-next-line react/jsx-handler-names
            onChange={e => {
              const value = e.currentTarget?.value

              if (handleValidateValue(value)) {
                handleChange(value)
              }
            }}
          />
        )
    }
  }

  return renderInput()
}

SearchAssistantInputValue.displayName = 'SearchAssistantInputValue'

export { SearchAssistantInputValue }
